import { dateFormat, getAge } from "../../core/utils/date-utils";
import { Alert, Button, Card, Checkbox, Col, Divider, Form, Input, Modal, Popover, Row, Select, Space, Spin, Switch, Tooltip, Typography } from "antd";
import ReportEditor from "../../core/components/ReportEditor";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ApiService from "../../core/service/api-service";
import Flex from "../../core/components/Flex";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import { useApp } from "../../core/context/AppContext";
import useRequest from "../../core/utils/useRequest";
import { studyTaskTypeKey } from "../../enums/StudyTaskTypeEnum";
import TaskResolutionField, { hasOpenReAssessment, OpenTasksBox } from "../study/TaskResolutionField";
import { getBoldTexts, hasBoldText, hasReportText } from "../../core/components/report-content";
import StudyUserAssignModal from "../study/StudyUserAssignModal";
import './report-create-edit-form.css'

// Patolojik bulgu, Bulgular bölümünden türetilir: hekim patolojik bulguyu kalın yazar.
// Şablondan gelen kalın parçalar (ör. kalın organ başlıkları) sayılmaz — templateBoldTexts.
// Bölüm boşken karar verilemez — "belirtilmemiş" (null) kalır.
//
// Hesap hiçbir koşulda kaydetmeyi bloklamaz: bozuk/beklenmedik HTML'de hata yutulur, log basılır
// ve false ile devam edilir. Rapor metnini kaybetmektense yanlış bir bayrak yazmak yeğdir; değer
// zaten yetkili kullanıcı tarafından önizlemeden düzeltilebiliyor.
const derivePathologicalFinding = (bulgular, templateBoldTexts = null) => {
    try {
        return hasReportText(bulgular) ? hasBoldText(bulgular, templateBoldTexts) : null;
    }
    catch (e) {
        console.error('Patolojik bulgu hesaplanamadı, false olarak devam ediliyor', e)
        return false;
    }
}

// Patolojik bulgu yalnız rapor bu ekranda ŞABLONDAN yazılırken hesaplanır (context.pathological.templateBoldTexts);
// otomatik kayıt raporu ilk harfte oluşturduğu için hesap ekran kapanana kadarki tüm kayıtlarda sürer.
// Daha önce kaydedilmiş bir rapor yeniden açıldığında şablon bilinmez: kayıtlı değer (elle verildiyse
// o işaretiyle birlikte) olduğu gibi geri gönderilir (context.pathological.saved).
// Bağlam yoksa (ör. rapor yüklenemedi) eski davranış: şablon hesaba katılmadan kalın metne bakılır.
const resolvePathologicalFinding = (context, bulgular) => {
    const state = context?.pathological
    if (state?.saved)
        return state.saved

    return {
        hasPathologicalFinding: derivePathologicalFinding(bulgular, state?.templateBoldTexts),
        pathologicalFindingIsManual: false
    }
}

// OTOMATİK KAYDETME
// Hekim yazarken rapor arka planda, ONAYSIZ olarak kaydedilir — elle "Kaydet"in aynısı.
// Çözdüğü sorun: bir rapor yazılırken başka isteme geçildiğinde init() eski içeriği hem ekrandan
// hem kayıt tamponundan siliyor; yazılan metin sessizce kayboluyordu. Ekran kapanırken de aynısı.
//
// Sunucu tarafı ŞART: payload'daki autoSave=true iken ReportService.EditReport raporun TASLAK dosyasının
// üzerine yazar; elle kaydedilmiş/onaylı içeriği ise önce bir kez sürüme arşivler (ReportVersionArchive).
// Aksi halde her otomatik kayıt bir ReportVersion satırı, bir json dosyası ve bir StudyLog satırı üretir,
// tek rapor yüzlerce sürüm bırakırdı.
const AUTO_SAVE_SETTING_KEY = 'report-auto-save'
// Yazma durduktan sonra beklenen süre.
const AUTO_SAVE_IDLE_MS = 5000
// Aralıksız yazan hekimde debounce hiçbir zaman dolmaz; en geç bu aralıkla da kaydedilir.
const AUTO_SAVE_INTERVAL_MS = 30000
// Rapor yüklendikten sonra editörün kendi çıktısını geri yansıtması için tanınan süre.
// TinyMCE, kendisine verilen HTML'i yeniden biçimlendirip (etiket/boşluk normalizasyonu) geri
// yayınlıyor; bu yankı ham dosya içeriğiyle bire bir eşleşmediği için rapor SADECE AÇILDIĞINDA
// "değişmiş" görünür ve ekranı açan herkes raporun "değiştiren/değiştirme tarihi" alanlarını
// ezerdi. Bu pencere içinde gelen değişiklikler kullanıcı düzenlemesi sayılmaz, karşılaştırma
// tabanı olarak alınır.
const AUTO_SAVE_BASELINE_SETTLE_MS = 1500

const CONTENT_KEYS = ['Bulgular', 'SonucVeOneriler', 'Karsilastirma', 'Teknik']

// "İçerik değişti mi" sorusu olay sayarak YANITLANAMAZ: TinyMCE, içerik programla yüklenirken de
// (setcontent) onEditorChange tetikliyor — yani rapor açılır açılmaz "değişti" görünürdü.
// Bu yüzden karşılaştırma, sunucudaki hâlin imzasıyla yapılır.
const contentSignature = (content) => JSON.stringify(CONTENT_KEYS.map(k => content?.[k] ?? ''))

// headerSlot: ReportModal başlığının sağındaki DOM düğümü. İstem bilgisi ve otomatik kaydet anahtarı
// oraya portal edilir; rapor kartının üst çubuğunda yalnız şablon/okuyan seçimi ve kaydetme eylemleri kalır.
// onStudyRead / onRequestRestored: bu ekrandan çekime okundu konduğunda ya da silinmiş istem geri
// yüklendiğinde çağrılır (istem listesi tazelensin diye).
export default function ReportCreateEditForm({ studyId, request, onSaved, onStudyRead, onRequestRestored, title, headerSlot }) {
    const { t } = useTranslation();
    const { hasPermission } = useUser()
    const { addKeyEvent, removeKeyEvent, pressedKey } = useApp()
    const formRef = useRef();
    const [autoClose, setAutoClose] = useState(localStorage.getItem('autoClose') || false)
    const [confirmModalVisible, setConfirmModalVisible] = useState(false)
    const [confirmForm] = Form.useForm()

    const [_reportId, setReportId] = useState(request?.request?.reportId)
    const [readUsers, setReadUsers] = useState([])
    const [templates, setTemplates] = useState([])
    const [template, setTemplate] = useState({ content: {} });
    const [reportContent, setReportContent] = useState({});
    const [report, setReport] = useState({
        request: {
            requestType: {}
        }
    })
    const reportSaveContent = useRef({})

    // Elle kaydetme de bunu kullanır: otomatik kayıt raporu OLUŞTURMUŞ olabilir ve prop'taki
    // request.reportId hâlâ boş olabilir — o durumda elle kaydetme aynı isteme İKİNCİ bir rapor açardı.
    const reportIdRef = useRef(request?.reportId ?? null)
    // Bu ekranda oluşturulan raporların istem -> rapor eşlemesi. Otomatik kayıt grid'i tazelemiyor
    // (tazeleseydi hekim yazarken editör yeniden yüklenir, imleç başa atardı), dolayısıyla istem
    // satırı bir süre daha "raporsuz" görünüyor. Hekim o isteme geri döndüğünde raporunu buradan
    // buluruz; yoksa yazdığı metin yerine şablon açılır ve kaydetmede ikinci bir rapor oluşurdu.
    const createdReportIds = useRef(new Map())
    // Okuyan doktor seçimi: sökülme anında formRef null olabildiği için ayrıca ref'te tutulur.
    const readUserIdRef = useRef(null)

    // Otomatik kayıt, ekranda O AN duran istemin değil, içeriğin AİT OLDUĞU istemin bağlamına yazar:
    // başka isteme geçilirken tetiklenen kayıt hâlâ eski istemi hedefler.
    const autoSaveContext = useRef(null)
    const autoSaveTimer = useRef(null)
    // Tüm kaydetmeler tek sıraya alınır: paralel iki otomatik kayıt, henüz raporu olmayan bir
    // istemde İKİ ayrı rapor oluştururdu.
    const saveQueue = useRef(Promise.resolve())
    const autoSaveEnabledRef = useRef(false)
    const contentLoadingRef = useRef(false)
    const mountedRef = useRef(true)

    // Varsayılan AÇIK: ayarı hiç görmemiş kullanıcı da yazdığını kaybetmesin. Kapatmak bilinçli bir
    // tercihtir ve o tercih localStorage'da 'false' olarak durur.
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(() => localStorage.getItem(AUTO_SAVE_SETTING_KEY) !== 'false')
    // status: 'idle' | 'saving' | 'saved' | 'error'
    const [autoSaveStatus, setAutoSaveStatus] = useState({ status: 'idle', at: null })

    // İstem geçişlerinde GEÇ GELEN yanıtların ekranı ezmesini engelleyen tur numarası.
    // Kaydet -> hemen diğer isteme geç akışında, önceki istem için başlatılmış yüklemeler
    // (kaydetme sonrası loadReport, grid tazelemesi, şablon isteği) yeni istem yüklendikten
    // SONRA dönebiliyordu; sonuç: ekranda ve reportSaveContent'te önceki istemin raporu kalıp
    // yanlış isteme kaydedilebiliyordu. Her yükleme turu numarayı artırır, tur bittiğinde
    // numara değişmişse gelen içerik sessizce atılır.
    const loadToken = useRef(0)
    const currentRequestId = useRef(null)
    const [contentLoading, setContentLoading] = useState(false)

    // OKUNMAMIŞ ÇEKİM
    // İstem listesi hastanın yakın tarihli DİĞER çekimlerinin istemlerini de gösterir; o çekim
    // okunmamış olabilir. Sunucu okunmamış çekime rapor yazdırmaz (ReportService.CreateReport), bu
    // yüzden hekim yazmaya başlamadan burada durdurulur: başkası adına okundu koyma izni
    // (STUDY_SET_READ_USER) varsa okuyan doktor seçtirilir, yoksa editör ve kaydetme kilitli kalır.
    // Bu ekrandan okundu konan çekimler ayrıca tutulur: istem satırı grid tazelenene kadar eski değeri taşır.
    const [readStudyIds, setReadStudyIds] = useState([])
    const [readModalOpen, setReadModalOpen] = useState(false)
    const canSetReadUser = hasPermission(PERMISSIONS.STUDY_SET_READ_USER)
    const studyUnread = !!request?.id && request?.isRead === false && !readStudyIds.includes(studyId)

    // SİLİNMİŞ İSTEM
    // İstem listesi silinmiş istemleri de gösterir. Sunucu silinmiş isteme rapor yazdırmaz
    // (ReportService.ThrowIfRequestIsDeleted); editör burada kilitlenir, geri alma izni olan kullanıcı
    // istemi uyarıdan geri yükleyebilir. Geri yüklenenler ayrıca tutulur: satır grid tazelenene kadar eski değeri taşır.
    const [restoredRequestIds, setRestoredRequestIds] = useState([])
    const canRestoreRequest = hasPermission(PERMISSIONS.STUDY_REQUEST_RESTORE_DELETED)
    const requestDeleted = !!request?.id && request?.isRequestDeleted === true && !restoredRequestIds.includes(request.id)
    // Rapor yazılamayan durumlar: editör, şablon/okuyan seçimi ve kaydetme kilitlenir.
    const writeLocked = studyUnread || requestDeleted

    const restoreRequest = async () => {
        const requestId = request.id
        const response = await ApiService.Request.RestoreRequest(studyId, requestId)
        if (!response.success)
            return

        setRestoredRequestIds(list => [...list, requestId])
        onRequestRestored && onRequestRestored(studyId, requestId)
    }

    const { run: runRestoreRequest, loading: restoring } = useRequest(restoreRequest)

    useEffect(() => {
        // Silinmiş istemde önce istem geri yüklenir; okundu sorusu ondan sonra.
        setReadModalOpen(studyUnread && !requestDeleted && !request?.reportIsConfirm && canSetReadUser)
    }, [request?.id, studyUnread, requestDeleted]);

    const setStudyRead = async (doctorUserId) => {
        const response = await ApiService.StudyDetails.AddRead({
            studyIdList: [studyId],
            isRead: true,
            readUserId: doctorUserId,
        })

        if (!response.success)
            return

        setReadStudyIds(list => [...list, studyId])
        setReadModalOpen(false)
        // Yeni okuma "Okuyan" alanında seçili gelsin.
        await getReadUsers()
        onStudyRead && onStudyRead(studyId)
    }

    const getReadUsers = async (token = loadToken.current) => {
        const res = await ApiService.StudyDetails.GetReadUsersOfStudy([studyId]);
        if (token !== loadToken.current)
            return;

        if (res.success) {
            setReadUsers(res.data)
        }
    }

    // fileRequest: çağıran isteği önceden (rapor kaydıyla paralel) başlatmış olabilir.
    const loadReportFileData = async (studyId, requestId, reportId, token = loadToken.current, fileRequest = null) => {
        try {
            const response = await (fileRequest ?? ApiService.Report.GetReportFileData(studyId, requestId, reportId));
            if (token !== loadToken.current)
                return {};

            if (response.success) {
                const content = JSON.parse(response.data);
                setReportContent(content)
                reportSaveContent.current = content;
                markContentSaved(content, { fromLoad: true })
                return content;
            }

            markContentLoadFailed()
        }
        catch (exp) {
            console.log(exp)
            markContentLoadFailed()
            if (token === loadToken.current)
                setReportContent({})
        }

        return {};
    }

    // ---- OTOMATİK KAYDETME ----------------------------------------------------------------

    // İçeriğin sunucudaki hâliyle aynı olduğunu işaretler; bundan sonra kullanıcı bir şey
    // değiştirmedikçe otomatik kayıt tetiklenmez.
    const markContentSaved = (content, { fromLoad = false } = {}) => {
        if (!autoSaveContext.current)
            return

        autoSaveContext.current.savedSignature = contentSignature(content)
        if (fromLoad) {
            // Başarılı yükleme, önceki başarısız denemenin kilidini kaldırır (ör. rapor yüklenemedi,
            // ardından hekim şablon seçti).
            autoSaveContext.current.loadFailed = false
            autoSaveContext.current.baselineUntil = Date.now() + AUTO_SAVE_BASELINE_SETTLE_MS
        }
    }

    // İçerik yüklenemedi: editörde raporun gerçek metni YOK. Bu bağlamda otomatik kayıt tamamen
    // kapatılır — aksi halde boş/eksik ekran içeriği raporun üzerine yazılabilirdi.
    const markContentLoadFailed = () => {
        if (autoSaveContext.current)
            autoSaveContext.current.loadFailed = true
    }

    // Tüm kaydetmeler (elle ve otomatik) buradan geçer: aynı anda iki kayıt çalışırsa, raporu
    // henüz olmayan bir isteme iki ayrı rapor açılırdı.
    const enqueueSave = (job) => {
        const next = saveQueue.current.then(job, job)
        // Zincir tek bir hatayla kırılmasın: sonraki kayıt yine de çalışmalı.
        saveQueue.current = next.catch(() => {})
        return next
    }

    const setAutoSaveStatusFor = (context, next) => {
        // Önceki isteme ait bir kayıt, ekranda duran YENİ istemin durumunu yazmasın.
        if (mountedRef.current && autoSaveContext.current === context)
            setAutoSaveStatus(next)
    }

    /**
     * Bir bağlamı (istem + rapor) sessizce kaydeder. Kaydedecek bir şey yoksa hiçbir istek atmaz.
     * `context` ve `content` dışarıdan verilebiliyor: istem değişirken ÖNCEKİ istemin içeriği
     * temizlenmeden önce anlık görüntüsü alınıp bu fonksiyona geçiliyor.
     */
    const runAutoSave = ({
        context = autoSaveContext.current,
        content = reportSaveContent.current,
        readUserId,
        fromInterval = false
    } = {}) => {
        if (!autoSaveEnabledRef.current)
            return

        if (!hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT))
            return

        if (!context?.requestId || context.reportIsConfirm || context.loadFailed)
            return

        // Hata sonrası her 30 sn'de bir yeniden denemek, sunucu erişilemezken bildirim yağmuru
        // demek. Yeniden deneme, kullanıcı yazmaya devam ettiğinde (debounce) olur.
        if (fromInterval && context.failed)
            return

        const signature = contentSignature(content)
        if (signature === context.savedSignature)
            return

        // Boşalmış içerik otomatik yazılmaz: içerik yüklenemediğinde ya da editör kurulmadan
        // tetiklendiğinde dolu bir raporun üzerine boş metin yazmak rapor kaybı demek olurdu.
        // Raporu kasten boşaltmak elle kaydetmeyle yapılır.
        if (CONTENT_KEYS.every(key => !hasReportText(content?.[key])))
            return

        const studyReadUserId = readUserId ?? readUserIdRef.current
        if (!studyReadUserId)
            return

        setAutoSaveStatusFor(context, { status: 'saving', at: null })

        return enqueueSave(async () => {
            try {
                const payload = {
                    content,
                    studyReadUserId,
                    isConfirm: false,
                    language: context.language,
                    ...resolvePathologicalFinding(context, content?.Bulgular),
                    autoSave: true
                }

                const response = context.reportId
                    ? await ApiService.Report.Edit(context.studyId, context.requestId, context.reportId, payload, { silent: true })
                    : await ApiService.Report.Create(context.studyId, context.requestId, payload, { silent: true })

                if (!response?.success) {
                    context.failed = true
                    setAutoSaveStatusFor(context, { status: 'error', at: new Date() })
                    return
                }

                context.failed = false
                context.savedSignature = signature

                if (!context.reportId) {
                    context.reportId = response.data
                    createdReportIds.current.set(context.requestId, response.data)
                    // Ekranda hâlâ aynı istem duruyorsa elle kaydetme de artık bu raporu DÜZENLEMELİ.
                    if (autoSaveContext.current === context)
                        reportIdRef.current = response.data
                }

                setAutoSaveStatusFor(context, { status: 'saved', at: new Date() })
            }
            catch (e) {
                console.error('Rapor otomatik kaydedilemedi', e)
                context.failed = true
                setAutoSaveStatusFor(context, { status: 'error', at: new Date() })
            }
        })
    }

    // Zamanlayıcılar ve sökülme kapanışı ilk render'ın kapanışını hapsetmesin: izin bilgisi,
    // çeviri gibi bağlamdan gelen değerler sonradan dolabiliyor.
    const runAutoSaveRef = useRef(runAutoSave)
    runAutoSaveRef.current = runAutoSave

    // Editörde her değişiklikte çağrılır; yazma durduktan AUTO_SAVE_IDLE_MS sonra kaydeder.
    const scheduleAutoSave = () => {
        if (!autoSaveEnabledRef.current)
            return

        const context = autoSaveContext.current

        // Yükleme sürerken ya da yükleme yankısı penceresindeyken gelen değişiklik kullanıcının
        // değil, editörün kendi çıktısıdır: kaydetme değil, karşılaştırma tabanı güncellenir.
        if (contentLoadingRef.current || (context && Date.now() < (context.baselineUntil ?? 0))) {
            markContentSaved(reportSaveContent.current)
            return
        }

        clearTimeout(autoSaveTimer.current)
        autoSaveTimer.current = setTimeout(() => runAutoSaveRef.current(), AUTO_SAVE_IDLE_MS)
    }

    useEffect(() => {
        autoSaveEnabledRef.current = autoSaveEnabled
        localStorage.setItem(AUTO_SAVE_SETTING_KEY, autoSaveEnabled ? 'true' : 'false')

        if (!autoSaveEnabled) {
            clearTimeout(autoSaveTimer.current)
            setAutoSaveStatus({ status: 'idle', at: null })
            return
        }

        const id = setInterval(() => runAutoSaveRef.current({ fromInterval: true }), AUTO_SAVE_INTERVAL_MS)
        return () => clearInterval(id)
    }, [autoSaveEnabled]);

    useEffect(() => {
        contentLoadingRef.current = contentLoading
    }, [contentLoading]);

    // Ekran kapanırken (modal kapanışı da buraya düşer) bekleyen değişiklik kaydedilir.
    // İstek iptal edilmiyor: fetch sökülmeyle durmuyor.
    useEffect(() => {
        return () => {
            mountedRef.current = false
            clearTimeout(autoSaveTimer.current)
            runAutoSaveRef.current()
        }
    }, []);

    // ---------------------------------------------------------------------------------------

    const onTemplateChange = async (val, option) => {
        setTemplate(option)
        if (autoSaveContext.current)
            autoSaveContext.current.language = option?.language ?? autoSaveContext.current.language
        await loadReportTemplateFile(option.id);
    }

    const loadReportTemplateFile = async (templateId, token = loadToken.current) => {
        try {
            if (templateId) {
                const response = await ApiService.ReportTemplate.GetReportTemplateFileData(templateId);
                if (token !== loadToken.current)
                    return;

                if (response.success) {
                    const content = JSON.parse(response.data);
                    setReportContent(content)
                    reportSaveContent.current = content
                    // Rapor bundan sonra bu şablondan yazılır: patolojik bulgu şablonun kalın parçaları
                    // dışındaki kalın metne göre hesaplanır (kayıtlı raporda şablon değiştirilirse de).
                    if (autoSaveContext.current)
                        autoSaveContext.current.pathological = { templateBoldTexts: getBoldTexts(content?.Bulgular) }
                    // Şablon içeriği "kaydedilmiş" sayılır: hekim tek harf yazmadıysa otomatik kayıt
                    // tetiklenmez, yani boş bir şablon yüzünden isteme rapor açılmaz.
                    markContentSaved(content, { fromLoad: true })
                    return;
                }
            }

            if (token === loadToken.current) {
                setReportContent({})
                // Şablonsuz yeni rapor: yok sayılacak kalın parça yok, ama yine de "bu ekranda yazılan" rapor.
                if (autoSaveContext.current)
                    autoSaveContext.current.pathological = { templateBoldTexts: new Set() }
                markContentSaved({}, { fromLoad: true })
            }
        }
        catch (e) {
            markContentLoadFailed()
            if (token === loadToken.current)
                setReportContent({})
        }
    }

    const loadReport = async (requestId, reportId, token = loadToken.current) => {
        // Karar prop'a değil, verilen parametrelere göre: bu ekranda yeni oluşturulmuş bir rapor
        // prop'a henüz yansımamış olabiliyor.
        if (requestId && reportId) {
            // Rapor kaydı ve içerik dosyası birbirini beklemeden istenir; içerik yine yalnız kayıt
            // başarıyla geldiyse uygulanır.
            const fileRequest = ApiService.Report.GetReportFileData(studyId, requestId, reportId)
            // Kayıt başarısızsa dosya isteği hiç beklenmez: reddi yakalanmamış hata olarak düşmesin.
            fileRequest.catch(() => {})

            const response = await ApiService.Report.Get(studyId, requestId, reportId)
            if (token !== loadToken.current)
                return;

            if (response.success) {
                setTemplate({})
                setReport(response.data)
                const context = autoSaveContext.current?.requestId === requestId ? autoSaveContext.current : null
                if (context) {
                    context.language = response.data?.language ?? context.language
                    // Kaydetme sonrası yeniden yüklemede şablondan yazılan rapor hesaplanmaya devam eder;
                    // yalnızca kayıtlı olarak AÇILAN raporun değeri korunur.
                    if (!context.pathological?.templateBoldTexts)
                        context.pathological = {
                            saved: {
                                hasPathologicalFinding: response.data?.hasPathologicalFinding ?? null,
                                pathologicalFindingIsManual: !!response.data?.pathologicalFindingIsManual
                            }
                        }
                }
                await loadReportFileData(studyId, requestId, reportId, token, fileRequest)
            }
        }
    }

    // Çekimin EN SON okuması. Dizi konumuna (readUsers[length-1]) güvenmek yerine readDate'e
    // bakılır: sunucu artık ReadDate'e göre sıralı dönüyor ama varsayılanın hangi okuma olduğu
    // ekranda görünmeyen bir sıralamaya bağlı kalmamalı — yanlış hekim seçili gelirse rapor
    // yanlış okuyana yazılır.
    const getLastReadUser = (list) => {
        if (!list?.length)
            return null;

        return list.reduce((latest, x) =>
            new Date(x?.readDate || 0) >= new Date(latest?.readDate || 0) ? x : latest
        );
    }

    useEffect(() => {
        // Kayıtlı raporun okuyanı her zaman kazanır: mevcut bir rapor açılıyorsa seçim
        // değiştirilmemeli, yoksa kaydetmede okuyan sessizce değişirdi.
        if (report?.studyReadUser?.id) {
            formRef.current.setFieldsValue({
                studyReadUserId: report?.studyReadUser?.id
            })
            readUserIdRef.current = report?.studyReadUser?.id
            return
        }

        const lastRead = getLastReadUser(readUsers)
        if (lastRead?.id) {
            formRef.current.setFieldsValue({
                studyReadUserId: lastRead.id
            })
            readUserIdRef.current = lastRead.id
        }
        // `report` de bağımlılık: init() içinde önce getReadUsers, SONRA loadReport bitiyor.
        // Yalnızca [readUsers] olsaydı rapor yüklendiğinde efekt yeniden çalışmaz ve mevcut
        // raporu açarken kayıtlı okuyan yerine "son okuma" seçili kalırdı.
    }, [readUsers, report])

    const previewPdf = async () => {
        const response = await ApiService.ReportGenerator.GetReportPreviewPdf(studyId, request?.id, request?.reportId, {
            ...reportSaveContent.current
        })

        if (response.success) {
            console.log(response.data)
            const pdfBase64 = response.data.pdfFile;
            const pdfWindow = window.open("");
            pdfWindow.document.write(
                `<iframe width='100%' height='100%' src='data:application/pdf;base64,${pdfBase64}'></iframe>`
            );
        }
    }

    const save = async (isConfirm, confirmDescription = null) => {
        if (!request?.id || request?.reportIsConfirm || writeLocked)
            return

        // Bekleyen otomatik kayıt iptal: içerik zaten şimdi kaydediliyor.
        clearTimeout(autoSaveTimer.current)

        // İçerik hâlâ yükleniyorken kaydetmek, editörde henüz önceki istemin metni varken
        // onu bu isteme yazmak demek — kısayolla (Alt+Q / Ctrl+Q) tetiklenebildiği için burada da bloklanır.
        if (contentLoading)
            return

        if (!hasPermission(PERMISSIONS.REPORT_CONFIRM) && isConfirm)
            return

        // Kaydetme sırasında kullanıcı başka isteme geçerse, aşağıdaki yeniden yükleme
        // artık ekrandaki isteme ait değildir; tur numarası ile atılır.
        const token = loadToken.current

        const text = formRef.current.getFieldsValue().text;
        const studyReadUserId = formRef.current.getFieldsValue().studyReadUserId;
        // Patolojik bulgu YALNIZCA kaydederken hesaplanır. Rapor yazan kullanıcı bu alanı görmez;
        // yetkili kullanıcı rapor önizlemesinden elle değiştirebilir (o zaman isManual=true olur).
        const pathologicalContext = autoSaveContext.current?.requestId === request?.id ? autoSaveContext.current : null
        const pathologicalFinding = resolvePathologicalFinding(pathologicalContext, reportSaveContent.current?.Bulgular);

        if (text)
            await ApiService.StudyAnamnez.EditClinicalInformation(studyId, text)

        // İçeriğin anlık görüntüsü: kayıt sırasında hekim yazmaya devam ederse imza ile gönderilen
        // metin birbirini tutmalı, yoksa yazılan ama gönderilmeyen metin "kaydedildi" sayılırdı.
        const content = reportSaveContent.current

        // Sıraya alınır: o an süren bir otomatik kayıt varsa önce o biter.
        return enqueueSave(async () => {
            // request.reportId DEĞİL ve sıraya girmeden ÖNCE değil: sırada bekleyen otomatik kayıt
            // bu istem için raporu yeni oluşturmuş olabilir; eski değerle burada ikinci bir rapor açılırdı.
            const reportId = reportIdRef.current ?? request?.reportId

            const payload = {
                content,
                studyReadUserId,
                isConfirm,
                language: template.language || report?.language,
                description: confirmDescription,
                ...pathologicalFinding
            }

            const response = reportId
                ? await ApiService.Report.Edit(studyId, request?.id, reportId, payload)
                : await ApiService.Report.Create(studyId, request?.id, payload)

            if (response.success) {
                reportIdRef.current = response.data
                createdReportIds.current.set(request?.id, response.data)
                if (autoSaveContext.current?.requestId === request?.id) {
                    autoSaveContext.current.reportId = response.data
                    autoSaveContext.current.failed = false
                    autoSaveContext.current.savedSignature = contentSignature(content)
                }
                if (mountedRef.current)
                    setAutoSaveStatus({ status: 'idle', at: null })

                onSaved && onSaved(studyId, request?.id, response.data);
            }

            await loadReport(request?.id, response.data, token)
        })
    }

    // Kaydetme sırasında geri bildirim tüm ekranı karartmak yerine butonun kendisinde verilir.
    // antd Button `loading` iken tıklamayı yok saydığı için bu aynı zamanda çift kaydı da engeller.
    // İki ayrı sarmalayıcı: "Kaydet"e basınca yalnız o buton döner, "Kaydet ve Onayla" değil.
    const {run: runSave, loading: saving} = useRequest(save)
    const {run: runSaveAndConfirm, loading: confirming} = useRequest(save)
    // Önizleme PDF'i sunucuda üretiliyor, birkaç saniye sürebiliyor.
    const {run: runPreviewPdf, loading: previewing} = useRequest(previewPdf)

    // Onay yalnız yeniden değerlendirmeyi kapatır (sonucu sorulur); eksik görüntü onayla kapanmaz, soru da sorulmaz.
    const handleSaveAndConfirmClick = async () => {
        if (hasOpenReAssessment(request?.studyTasks)) {
            setConfirmModalVisible(true);
        } else {
            await runSaveAndConfirm(true);
        }
    }

    const handleConfirmWithDescription = async () => {
        try {
            const values = await confirmForm.validateFields();
            await runSaveAndConfirm(true, values.description);
            setConfirmModalVisible(false);
            confirmForm.resetFields();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    const getReportTemplates = async (studyId, requestId) => {
        const response = await ApiService.ReportTemplate.GetList(studyId, requestId);
        if (response.success) {
            return response.data.map(t => {
                return {
                    ...t,
                    value: t.id,
                    label: t.name
                }
            })
        }

        return []
    }

    const init = async () => {
        const token = ++loadToken.current
        const requestId = request?.id ?? null
        const requestChanged = currentRequestId.current !== requestId
        currentRequestId.current = requestId

        // Başka isteme geçiliyor: önceki istemin yazılmış ama kaydedilmemiş içeriği aşağıdaki
        // temizlikte yok olacak. Kayıt burada, temizlikten ÖNCE ve ESKİ bağlamla kuyruğa alınır.
        // İçeriğin anlık görüntüsü şimdi alınıyor; editör sıfırlanırken tampon yeni bir nesneyle
        // değiştirildiği için buradaki referans bozulmuyor.
        if (requestChanged && autoSaveContext.current) {
            clearTimeout(autoSaveTimer.current)
            runAutoSave({
                context: autoSaveContext.current,
                content: reportSaveContent.current,
                readUserId: readUserIdRef.current
            })
        }

        // Başka bir isteme geçildiği anda önceki istemin içeriği hem ekrandan hem de kayıt
        // tamponundan silinir; yenisi gelene kadar editör boş ve kilitli kalır.
        if (requestChanged) {
            setReportContent({})
            reportSaveContent.current = {}
            setTemplates([])
            setTemplate({ content: {} })
            setReport({ request: { requestType: {} } })
            readUserIdRef.current = null
            reportIdRef.current = null
            autoSaveContext.current = null
            setAutoSaveStatus({ status: 'idle', at: null })
        }

        if (!requestId) {
            setContentLoading(false)
            reportIdRef.current = null
            return
        }

        // Otomatik kaydın hedefi: bu istem ve (varsa) bu rapor. İçerik yüklendikçe
        // savedSignature / language güncellenir.
        reportIdRef.current = request?.reportId ?? createdReportIds.current.get(requestId) ?? reportIdRef.current ?? null
        if (!autoSaveContext.current || autoSaveContext.current.requestId !== requestId) {
            autoSaveContext.current = {
                studyId,
                requestId,
                reportId: reportIdRef.current,
                reportIsConfirm: !!request?.reportIsConfirm,
                language: null,
                savedSignature: contentSignature({}),
                loadFailed: false,
                failed: false,
                // Şablon ya da kayıtlı rapor yüklenince dolar; bkz. resolvePathologicalFinding.
                pathological: null
            }
        }
        else {
            autoSaveContext.current.reportId = reportIdRef.current
            autoSaveContext.current.reportIsConfirm = !!request?.reportIsConfirm
        }

        if (requestChanged)
            setContentLoading(true)

        try {
            // Birbirini beklemeyen yüklemeler paralel gider (eskiden okuyanlar → şablonlar → rapor → dosya sırayla).
            // allSettled: biri hata verse bile diğerleri bitmeden finally'ye düşülmez; contentLoading
            // (editör ve kaydetme kilidi) içerik hazır olana kadar açık kalır — istem değiştirirken
            // içerik gelmeden rapor yazılmaya başlanamaz.
            const reportId = reportIdRef.current
            const results = await Promise.allSettled([
                getReportTemplates(studyId, requestId),
                getReadUsers(token),
                reportId ? loadReport(requestId, reportId, token) : null,
            ])
            if (token !== loadToken.current)
                return

            const templates = results[0].status === 'fulfilled' ? results[0].value : []
            setTemplates(templates)

            const failed = results.find(x => x.status === 'rejected')
            if (failed)
                throw failed.reason

            // Rapor yoksa varsayılan şablonun içeriği; şablon listesine bağlı olduğu için ondan sonra.
            if (!reportId) {
                let templateId = templates[0]?.id;
                await loadReportTemplateFile(templateId, token)
            }
        }
        finally {
            if (token === loadToken.current)
                setContentLoading(false)
        }
    }

    useEffect(() => {
        init()
    }, [request])

    useEffect(() => {
        addKeyEvent([
            { keyCode: 'KeyQ', modifier: 'ControlLeft', prevent: false, },
            { keyCode: 'KeyQ', modifier: 'AltLeft', prevent: false, },
            // {keyCode: 'F4', modifier: 'ControlLeft', prevent: true,},
        ])

        return () => {
            removeKeyEvent([
                { keyCode: 'KeyQ', modifier: 'ControlLeft', prevent: false, },
                { keyCode: 'KeyQ', modifier: 'AltLeft', prevent: false, },
            ])
        }
    }, []);

    useEffect(() => {
        if (pressedKey?.key?.code === 'KeyQ' && pressedKey?.modifier?.code === 'ControlLeft') {
            // console.log('Pressed CRTL+KeyQ')
            handleSaveAndConfirmClick()
        }
        else if (pressedKey?.key?.code === 'KeyQ' && pressedKey?.modifier?.code === 'AltLeft') {
            runSave(false)
        }
    }, [pressedKey]);

    // Modal başlığındaki hasta bilgisiyle aynı biçim: üstte gri kalın etiket, altta değer (bkz. StudyInfo variant="header").
    const headerField = (label, value) => (
        <div className="rcef-header-field">
            <Typography.Text style={{ fontWeight: 'bold', fontSize: 11, lineHeight: 1.2, color: '#8c8c8c' }}>{label}</Typography.Text>
            <Typography.Text style={{ fontSize: 13, lineHeight: 1.2 }}>{value}</Typography.Text>
        </div>
    )

    const headerContent = (
        <div className="rcef-header">
            {request?.id && (
                <>
                    {headerField(t('Request'), (
                        <Tooltip title={request.requestTypeName}>
                            <span className="rcef-header-request">{request.requestTypeName || '-'}</span>
                        </Tooltip>
                    ))}
                    {request?.accessionNumber && headerField(t('Accession No'), request.accessionNumber)}
                    {request?.requestDate && headerField(t('Date'), new Date(request.requestDate).toLocaleDateString())}
                    {request?.requestingPhysicianName && headerField(t('Physician'), request.requestingPhysicianName)}
                </>
            )}
            {
                hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT) && (
                    <div className="rcef-header-auto-save">
                        <Tooltip title={t('Auto Save Hint')}>
                            <label className="rcef-auto-save">
                                <Switch
                                    size={'small'}
                                    checked={autoSaveEnabled}
                                    onChange={(checked) => setAutoSaveEnabled(checked)}
                                />
                                {t('Auto Save')}
                            </label>
                        </Tooltip>
                        {
                            autoSaveEnabled && autoSaveStatus.status !== 'idle' && (
                                <Typography.Text
                                    type={autoSaveStatus.status === 'error' ? 'danger' : 'secondary'}
                                    style={{ fontSize: 11, lineHeight: 1.2 }}
                                >
                                    {autoSaveStatus.status === 'saving' && t('Auto Saving')}
                                    {autoSaveStatus.status === 'saved' && `${t('Auto Saved')} ${autoSaveStatus.at?.toLocaleTimeString()}`}
                                    {autoSaveStatus.status === 'error' && t('Auto Save Failed')}
                                </Typography.Text>
                            )
                        }
                    </div>
                )
            }
        </div>
    )

    return (
        <>
            {headerSlot && createPortal(headerContent, headerSlot)}
            <Form
                ref={formRef}
                layout={'vertical'}
                onValuesChange={(changed) => {
                    // Otomatik kayıt sökülme anında da çalışabiliyor; o anda formRef null olabilir.
                    if ('studyReadUserId' in changed)
                        readUserIdRef.current = changed.studyReadUserId
                }}
            >
                {/* Üst çubuk (kaydırmada yapışık), tek satır: solda şablon + okuyan, sağda rapor eylemleri.
                    Asıl son adım onay olduğu için tek birincil buton "Kaydet ve Onayla"dır; onay yetkisi yoksa "Kaydet". */}
                <div className="rcef-bar">
                    <div className="rcef-actions">
                        <div className="rcef-settings">
                            <Select
                                className="rcef-template"
                                size={'small'}
                                disabled={!request?.id || request?.reportIsConfirm || contentLoading || writeLocked}
                                placeholder={t('Report Template')}
                                value={template.value}
                                options={templates}
                                onChange={onTemplateChange}
                            />
                            <Form.Item
                                className="rcef-read-user"
                                name={'studyReadUserId'}
                                required={true}
                            >
                                <Select
                                    size={'small'}
                                    disabled={!request?.id || request?.reportIsConfirm || contentLoading || writeLocked}
                                    style={{ width: '100%' }}
                                    placeholder={t('Read User')}
                                >
                                    {
                                        readUsers.map(x => (
                                            <Select.Option key={x.id} value={x.id}>
                                                {x?.user?.visibleName}
                                            </Select.Option>
                                        ))
                                    }
                                </Select>
                            </Form.Item>
                        </div>
                        <div className="rcef-buttons">
                            <Button
                                size={'small'}
                                disabled={!request?.id || contentLoading}
                                loading={previewing}
                                onClick={() => runPreviewPdf()}
                            >
                                {t('Preview')}
                            </Button>
                            <Button
                                size={'small'}
                                disabled={!request?.id || request?.reportIsConfirm || confirming || contentLoading || writeLocked}
                                loading={saving}
                                onClick={async () => {
                                    await runSave(false)
                                }}
                                type={hasPermission(PERMISSIONS.REPORT_CONFIRM) ? 'default' : 'primary'}>
                                {t('Save') + ' (Alt + Q)'}
                            </Button>
                            {
                                hasPermission(PERMISSIONS.REPORT_CONFIRM) && (
                                    <Button
                                        size={'small'}
                                        disabled={!request?.id || request?.reportIsConfirm || saving || contentLoading || writeLocked}
                                        loading={confirming}
                                        onClick={handleSaveAndConfirmClick}
                                        type={'primary'}
                                    >
                                        {t('Save And Confirm') + ' (Ctrl + Q)'}
                                    </Button>
                                )
                            }
                        </div>
                    </div>

                    {requestDeleted && (
                        <Alert
                            type={'error'}
                            showIcon={true}
                            message={t('DELETED_REQUEST_NO_REPORT')}
                            action={canRestoreRequest && (
                                <Button size={'small'} loading={restoring} onClick={() => runRestoreRequest()}>
                                    {t('Restore')}
                                </Button>
                            )}
                        />
                    )}

                    {studyUnread && !requestDeleted && !request?.reportIsConfirm && (
                        <Alert
                            type={'error'}
                            showIcon={true}
                            message={t('UNREAD_STUDY_NO_REPORT')}
                            action={canSetReadUser && (
                                <Button size={'small'} onClick={() => setReadModalOpen(true)}>
                                    {t('Set Read')}
                                </Button>
                            )}
                        />
                    )}

                    {request?.studyTasks && request.studyTasks.length > 0 && !request?.reportIsConfirm && (
                        <div style={{ padding: 12, backgroundColor: 'var(--warn-soft)', borderRadius: 4, border: '1px solid var(--warn-border)' }}>
                            <strong style={{ color: 'var(--warn-text)' }}>{t('Open Tasks')}:</strong>
                            {request.studyTasks.map((task, idx) => (
                                <div key={idx} style={{ marginTop: 8 }}>
                                    <div style={{ fontWeight: 500 }}>{t(studyTaskTypeKey(task.taskType))}</div>
                                    {task.description && <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{task.description}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Spin spinning={contentLoading} tip={t('Loading')}>
                <div
                    style={{
                        padding: '4px 12px 12px',
                        minHeight: 240
                    }}
                >
                        <ReportEditor
                            editable={request?.id}
                            disabled={contentLoading || writeLocked}
                            defaultReportContent={request?.id ? reportContent : {}}
                            onBulgular={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    Bulgular: data
                                }
                                scheduleAutoSave()
                            }}
                            onSonuc={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    SonucVeOneriler: data
                                }
                                scheduleAutoSave()
                            }}
                            onKarsilastirma={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    Karsilastirma: data
                                }
                                scheduleAutoSave()
                            }}
                            onTeknik={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    Teknik: data
                                }
                                scheduleAutoSave()
                            }}
                        />
                </div>
                </Spin>
            </Form>
            <Modal
                title={t('Confirm Report - Open Task Exists')}
                open={confirmModalVisible}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    confirmForm.resetFields();
                }}
                onOk={handleConfirmWithDescription}
                confirmLoading={confirming}
                okText={t('Confirm')}
                cancelText={t('Cancel')}
            >
                <OpenTasksBox tasks={request?.studyTasks} />
                <Form form={confirmForm} layout="vertical">
                    <TaskResolutionField tasks={request?.studyTasks} />
                </Form>
            </Modal>
            <Modal
                title={t('Doctors')}
                open={readModalOpen}
                onCancel={() => setReadModalOpen(false)}
                footer={null}
                destroyOnClose={true}
            >
                <StudyUserAssignModal
                    selectDoctor={true}
                    autoSelect={true}
                    excludeSelf={!hasPermission(PERMISSIONS.STUDY_SET_READ)}
                    onSelected={async ({ doctor }) => {
                        await setStudyRead(doctor)
                    }}
                />
            </Modal>
        </>
    )
}
